import { useCallback, useContext, useEffect, useState } from 'react';

import { UserContext } from '~/auth/userContext';
import type { PeriodWindowRange } from '~/components/serviceHistory/periodWindow';
import { PeriodDetailModal } from '~/components/visitsCompletion/PeriodDetailModal';
import { useMaintenanceVisitPeriods } from '~/data/hooks/useMaintenanceVisitPeriods';
import type { MaintenanceVisitPeriod } from '~/data/schemas/maintenanceVisit';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { canAccess, MARK_MAINTENANCE_NOT_PERFORMED } from '~/utils/role';

export function PeriodCellModal({
  carId,
  periodStart,
  range,
  onClose,
  onChanged,
}: {
  carId: string;
  periodStart: string;
  range: PeriodWindowRange;
  onClose: () => void;
  onChanged: () => void;
}) {
  // The same window the grid was drawn from. Fetching the API default instead would
  // find no period matching a cell the user clicked in a year view.
  const visits = useMaintenanceVisitPeriods({ car: carId, ...range });
  const { userDetails } = useContext(UserContext);
  // The server holds these writes to a consultant or a LiftAI admin, while the tab
  // itself is open to a client. Without the same test here the client is offered a
  // button that answers 403.
  const canWrite =
    canAccess(userDetails.role, MARK_MAINTENANCE_NOT_PERFORMED) || userDetails.isLiftaiAdmin;
  const { showSnackbar } = useLAISnackbar();
  const [busy, setBusy] = useState(false);
  const period = visits.periods.find((row) => row.periodStart === periodStart) ?? null;
  // Settled with nothing to show. Without this the dialog simply never opens, the table
  // keeps the cell marked open, and clicking it again changes nothing.
  const foundNothing = !visits.isLoading && period === null;

  useEffect(() => {
    if (!foundNothing) return;
    showSnackbar(
      visits.error ? 'That period could not be loaded.' : 'That period is no longer there.',
      { variant: 'error' },
    );
    onClose();
  }, [foundNothing, onClose, showSnackbar, visits.error]);

  const act = useCallback(
    async (run: () => Promise<void>, failure: string) => {
      setBusy(true);
      try {
        await run();
        onChanged();
      } catch {
        showSnackbar(failure, { variant: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [onChanged, showSnackbar],
  );

  const onMarkNotPerformed = useCallback(
    (target: MaintenanceVisitPeriod) =>
      void act(
        () => visits.markNotPerformed(target.carId, target.periodStart),
        'That visit could not be marked as not performed.',
      ),
    [act, visits],
  );

  const onClearNotPerformed = useCallback(
    (target: MaintenanceVisitPeriod) =>
      void act(
        () => visits.clearNotPerformed(target.carId, target.periodStart),
        'That mark could not be removed.',
      ),
    [act, visits],
  );

  return (
    <PeriodDetailModal
      period={period}
      canWrite={canWrite}
      loading={visits.isLoading}
      busy={busy}
      onClose={onClose}
      onMarkNotPerformed={onMarkNotPerformed}
      onClearNotPerformed={onClearNotPerformed}
    />
  );
}
